import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { Project, SiteWalk, SiteWalkItem } from '../types'
import { fmtDate, todayISO } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

const STATUS_COLOR: Record<SiteWalkItem['status'], BadgeColor> = {
  pending: 'gray',
  ok: 'green',
  flagged: 'red',
  na: 'gray',
}

type AiFlag = { severity: 'info' | 'warning' | 'critical'; item: string; note: string }
const FLAG_COLOR: Record<AiFlag['severity'], BadgeColor> = {
  info: 'blue',
  warning: 'gold',
  critical: 'red',
}

export default function SiteWalkTab({ project }: { project: Project }) {
  const [walks, setWalks] = useState<SiteWalk[]>([])
  const [active, setActive] = useState<{ walk: SiteWalk; items: SiteWalkItem[] } | null>(null)
  const [templates, setTemplates] = useState<Record<string, string[]>>({})
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<SiteWalk>>({
    date: todayISO(),
    walked_by: 'Dustin',
  })

  useEffect(() => {
    api.siteWalks.list(project.id).then(setWalks)
    api.siteWalks.templates().then((d) => setTemplates(d.templates))
  }, [project.id])

  async function startWalk() {
    if (!draft.phase) return
    const walk = await api.siteWalks.create(project.id, draft)
    // Pre-populate checklist from template
    const template = templates[draft.phase] ?? []
    const items: SiteWalkItem[] = []
    for (let i = 0; i < template.length; i++) {
      const item = await api.siteWalks.addItem(walk.id, {
        checklist_item: template[i],
        order_index: i,
      })
      items.push(item)
    }
    setWalks((p) => [walk, ...p])
    setActive({ walk, items })
    setAdding(false)
    setDraft({ date: todayISO(), walked_by: 'Dustin' })
  }

  async function openWalk(id: string) {
    const data = await api.siteWalks.get(id)
    setActive({ walk: data.walk, items: data.items })
  }

  async function updateItem(item: SiteWalkItem, patch: Partial<SiteWalkItem>) {
    const updated = await api.siteWalks.updateItem(item.id, patch)
    if (active) {
      setActive({
        ...active,
        items: active.items.map((x) => (x.id === item.id ? updated : x)),
      })
    }
  }

  async function addCustomItem() {
    if (!active) return
    const text = window.prompt('New checklist item')
    if (!text) return
    const item = await api.siteWalks.addItem(active.walk.id, {
      checklist_item: text,
      order_index: active.items.length,
    })
    setActive({ ...active, items: [...active.items, item] })
  }

  // Notes save lives in NotesPanel — local draft + explicit Save button.
  // Walk-list mirror of saved notes is updated via the panel's onSaved callback.
  function updateWalk(updated: SiteWalk) {
    setActive((cur) => (cur ? { ...cur, walk: updated } : cur))
    setWalks((p) => p.map((w) => (w.id === updated.id ? updated : w)))
  }

  if (active) {
    const walk = active.walk
    const flagged = active.items.filter((i) => i.status === 'flagged').length
    const ok = active.items.filter((i) => i.status === 'ok').length
    const pending = active.items.filter((i) => i.status === 'pending').length
    return (
      <section>
        <div className="row-between mb-3">
          <div>
            <h2>{walk.phase} walk</h2>
            <p className="muted small">
              {fmtDate(walk.date)} · {walk.walked_by ?? '—'} · {ok} ok · {flagged} flagged ·{' '}
              {pending} pending
            </p>
          </div>
          <div className="row gap-2">
            <button className="btn-ghost" onClick={() => setActive(null)}>
              ← Back
            </button>
            <button
              className="btn-ghost small"
              onClick={async () => {
                if (
                  !window.confirm(
                    `Delete this ${walk.phase ?? 'walk'}? Photos, checklist items, and AI analysis are all removed. Cannot be undone.`
                  )
                )
                  return
                await api.siteWalks.delete(walk.id)
                setWalks((p) => p.filter((x) => x.id !== walk.id))
                setActive(null)
              }}
            >
              ✕ Delete walk
            </button>
          </div>
        </div>

        <PhotosPanel walk={walk} onUpdated={updateWalk} />
        <VoiceNotePanel walk={walk} onUpdated={updateWalk} />
        <AiPanel walk={walk} projectId={walk.project_id} onUpdated={updateWalk} />

        <div className="card mb-3">
          {active.items.map((item) => (
            <div key={item.id} className="row-list">
              <div className="grow">
                <div className="row-title">{item.checklist_item}</div>
                {item.notes && <div className="muted small mt-1">{item.notes}</div>}
              </div>
              <div className="row gap-2">
                <button
                  className={`btn-ghost small ${item.status === 'ok' ? 'active-ok' : ''}`}
                  onClick={() => updateItem(item, { status: 'ok' })}
                >
                  ✓ OK
                </button>
                <button
                  className={`btn-ghost small ${item.status === 'flagged' ? 'active-flag' : ''}`}
                  onClick={() => updateItem(item, { status: 'flagged' })}
                >
                  ⚠ Flag
                </button>
                <button
                  className={`btn-ghost small ${item.status === 'na' ? '' : ''}`}
                  onClick={() => updateItem(item, { status: 'na' })}
                >
                  N/A
                </button>
                <Badge color={STATUS_COLOR[item.status]}>{item.status.toUpperCase()}</Badge>
              </div>
            </div>
          ))}
          <div className="mt-3">
            <button className="btn-ghost small" onClick={addCustomItem}>
              + Custom checklist item
            </button>
          </div>
        </div>

        <NotesPanel walk={walk} onSaved={updateWalk} />
      </section>
    )
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Site Walks</h2>
          <p className="muted small">
            {walks.length} walks logged · end-of-day capture (30-45 min walk).
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Start Walk'}
        </button>
      </div>

      <div className="callout small mb-3">
        Phase-specific checklists pre-populate from Heritage standards. Mark each item OK,
        Flagged, or N/A. Phase 3 adds Claude-API photo analysis on top of this data.
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Phase</span>
            <select
              value={draft.phase ?? ''}
              onChange={(e) => setDraft({ ...draft, phase: e.target.value })}
            >
              <option value="">— select phase —</option>
              {Object.keys(templates).map((p) => (
                <option key={p} value={p}>
                  {p} ({templates[p].length} items)
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>Date</span>
            <input
              type="date"
              value={draft.date ?? todayISO()}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Walked by</span>
            <input
              value={draft.walked_by ?? ''}
              onChange={(e) => setDraft({ ...draft, walked_by: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Weather</span>
            <input
              value={draft.weather ?? ''}
              onChange={(e) => setDraft({ ...draft, weather: e.target.value })}
              placeholder="Sunny, 62°F"
            />
          </label>
          <button className="btn-primary" onClick={startWalk}>
            Start Walk →
          </button>
        </div>
      )}

      {walks.length === 0 && !adding && (
        <p className="muted">
          No walks yet. Click "Start Walk" to capture today's site walk with the phase checklist.
        </p>
      )}

      {walks.map((w) => (
        <div key={w.id} className="card mb-2" onClick={() => openWalk(w.id)} style={{ cursor: 'pointer' }}>
          <div className="row-between">
            <div>
              <div className="row-title">{w.phase ?? 'Walk'} · {fmtDate(w.date)}</div>
              <div className="muted small">
                {w.walked_by ?? '—'}
                {w.weather && ` · ${w.weather}`}
              </div>
              {w.general_notes && (
                <div className="muted small mt-2" style={{ maxWidth: 600 }}>
                  {w.general_notes.slice(0, 200)}
                  {w.general_notes.length > 200 && '…'}
                </div>
              )}
            </div>
            <div className="text-right">
              <Badge color="gray">→</Badge>
            </div>
          </div>
        </div>
      ))}
    </section>
  )
}

// ----- Voice note panel ----------------------------------------------------
//
// Browser MediaRecorder records mic input as webm/opus, uploads the resulting
// Blob to the Worker which stores it in R2. The walk row keeps the most
// recent key in voice_note_url; older recordings stay in R2 (no purge).

function VoiceNotePanel({
  walk,
  onUpdated,
}: {
  walk: SiteWalk
  onUpdated: (w: SiteWalk) => void
}) {
  const [recording, setRecording] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const tickRef = useRef<number | null>(null)

  async function start() {
    setErr(null)
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr('mic not available on this browser')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Pick a mime type the browser can record. Safari prefers mp4/m4a;
      // Chrome/Firefox prefer webm/opus.
      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ]
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? ''
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      chunksRef.current = []
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' })
        setPreviewBlob(blob)
        setPreviewUrl(URL.createObjectURL(blob))
      }
      recorderRef.current = rec
      rec.start()
      setRecording(true)
      setElapsed(0)
      tickRef.current = window.setInterval(() => setElapsed((s) => s + 1), 1000)
    } catch (e) {
      setErr((e as Error).message)
    }
  }

  function stop() {
    recorderRef.current?.stop()
    setRecording(false)
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
  }

  function discardPreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    setPreviewBlob(null)
  }

  async function upload() {
    if (!previewBlob) return
    setUploading(true)
    setErr(null)
    try {
      await api.siteWalks.uploadVoiceNote(walk.id, previewBlob)
      const refreshed = await api.siteWalks.get(walk.id)
      onUpdated(refreshed.walk)
      discardPreview()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setUploading(false)
    }
  }

  async function removeSaved() {
    if (!window.confirm('Remove the saved voice note?')) return
    await api.siteWalks.deleteVoiceNote(walk.id)
    const refreshed = await api.siteWalks.get(walk.id)
    onUpdated(refreshed.walk)
  }

  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const elapsedDisplay = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`

  return (
    <div className="card mb-3">
      <div className="row-between mb-2">
        <h3>Voice note</h3>
        <div className="row gap-2">
          {!recording && !previewBlob && (
            <button className="btn-primary small" onClick={start}>
              ● Record
            </button>
          )}
          {recording && (
            <button className="btn-primary small" onClick={stop}>
              ■ Stop ({elapsedDisplay})
            </button>
          )}
        </div>
      </div>

      {err && <div className="card alert mb-2">{err}</div>}

      {/* Live recording indicator */}
      {recording && (
        <p className="muted small">
          <span className="danger">●</span> Recording… {elapsedDisplay}. Hit Stop when done.
        </p>
      )}

      {/* Preview before upload */}
      {previewUrl && !recording && (
        <div>
          <p className="muted small mb-2">Preview your recording:</p>
          <audio src={previewUrl} controls style={{ width: '100%' }} />
          <div className="row gap-2 mt-2">
            <button className="btn-primary small" onClick={upload} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Save voice note'}
            </button>
            <button className="btn-ghost small" onClick={discardPreview} disabled={uploading}>
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Saved voice note (if no preview in flight) */}
      {!previewUrl && !recording && walk.voice_note_url && (
        <div>
          <audio src={api.fileUrl(walk.voice_note_url)} controls style={{ width: '100%' }} />
          <div className="row gap-2 mt-2">
            <button className="btn-ghost small" onClick={removeSaved}>
              ✕ Remove voice note
            </button>
            <span className="muted small">
              Recording a new one will replace this.
            </span>
          </div>
        </div>
      )}

      {!previewUrl && !recording && !walk.voice_note_url && (
        <p className="muted small">
          No voice note yet. Click Record to capture mic audio in the browser. Quicker than typing
          notes during a walk.
        </p>
      )}
    </div>
  )
}

// ----- Notes panel ---------------------------------------------------------

function NotesPanel({ walk, onSaved }: { walk: SiteWalk; onSaved: (w: SiteWalk) => void }) {
  const [draft, setDraft] = useState(walk.general_notes ?? '')
  const [saving, setSaving] = useState(false)
  const dirty = draft !== (walk.general_notes ?? '')

  // If the walk reloads (e.g. after analyze), reset draft to the saved value
  // unless the user is in the middle of editing.
  useEffect(() => {
    if (!dirty) setDraft(walk.general_notes ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walk.general_notes])

  async function save() {
    setSaving(true)
    try {
      const updated = await api.siteWalks.update(walk.id, { general_notes: draft })
      onSaved(updated)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card mb-3">
      <div className="row-between mb-2">
        <h3>General notes</h3>
        <div className="row gap-2">
          {dirty && (
            <button
              className="btn-ghost small"
              onClick={() => setDraft(walk.general_notes ?? '')}
              disabled={saving}
            >
              Discard
            </button>
          )}
          <button className="btn-primary small" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save notes' : 'Saved'}
          </button>
        </div>
      </div>
      <textarea
        rows={6}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="What you saw, who was on site, what you flagged for follow-up. End-of-day brain dump."
      />
    </div>
  )
}

// ----- Photos panel ---------------------------------------------------------

function PhotosPanel({
  walk,
  onUpdated,
}: {
  walk: SiteWalk
  onUpdated: (w: SiteWalk) => void
}) {
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const photoKeys: string[] = (() => {
    try {
      return walk.photo_urls ? (JSON.parse(walk.photo_urls) as string[]) : []
    } catch {
      return []
    }
  })()

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    setUploading(true)
    setErr(null)
    try {
      const result = await api.siteWalks.uploadPhotos(walk.id, files)
      // Refetch the walk to get the updated photo_urls
      const updated = await api.siteWalks.get(walk.id)
      onUpdated(updated.walk)
      // Bridge: also surface count to user
      void result
    } catch (ex) {
      setErr((ex as Error).message)
    } finally {
      setUploading(false)
      // Reset the input so the same file can be re-selected
      e.target.value = ''
    }
  }

  return (
    <div className="card mb-3">
      <div className="row-between mb-2">
        <h3>Photos ({photoKeys.length})</h3>
        <label className="btn-primary small" style={{ cursor: 'pointer' }}>
          {uploading ? 'Uploading…' : '+ Upload photos'}
          <input
            type="file"
            multiple
            accept="image/*"
            capture="environment"
            disabled={uploading}
            style={{ display: 'none' }}
            onChange={onFiles}
          />
        </label>
      </div>
      {err && <div className="card alert mb-2">{err}</div>}
      {photoKeys.length === 0 ? (
        <p className="muted small">
          No photos yet. Take photos with your phone — the upload supports your camera directly.
        </p>
      ) : (
        <div className="photo-grid">
          {photoKeys.map((key) => (
            <div key={key} className="photo-cell">
              <a href={api.fileUrl(key)} target="_blank" rel="noopener noreferrer">
                <img className="photo-thumb" src={api.fileUrl(key)} alt="walk photo" loading="lazy" />
              </a>
              <button
                className="photo-delete"
                title="Delete this photo"
                onClick={async () => {
                  if (!window.confirm('Delete this photo? Permanent.')) return
                  try {
                    await api.siteWalks.deletePhoto(walk.id, key)
                    const refreshed = await api.siteWalks.get(walk.id)
                    onUpdated(refreshed.walk)
                  } catch (e) {
                    setErr((e as Error).message)
                  }
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ----- AI panel -------------------------------------------------------------

function AiPanel({
  walk,
  projectId,
  onUpdated,
}: {
  walk: SiteWalk
  projectId: string
  onUpdated: (w: SiteWalk) => void
}) {
  const [running, setRunning] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [promoted, setPromoted] = useState<Set<number>>(new Set())

  const flags: AiFlag[] = (() => {
    try {
      return walk.ai_flags ? (JSON.parse(walk.ai_flags) as AiFlag[]) : []
    } catch {
      return []
    }
  })()

  async function promoteToPunch(idx: number, f: AiFlag) {
    if (promoted.has(idx)) return
    try {
      await api.punch.create(projectId, {
        description: f.item,
        notes: `From AI walk analysis (${walk.phase ?? 'walk'} ${walk.date.slice(0, 10)}): ${f.note}`,
        blocks_payment: f.severity === 'critical' ? 1 : 0,
        status: 'open',
      })
      setPromoted((p) => new Set(p).add(idx))
    } catch (ex) {
      setErr((ex as Error).message)
    }
  }

  async function analyze() {
    setRunning(true)
    setErr(null)
    try {
      await api.siteWalks.analyze(walk.id)
      const updated = await api.siteWalks.get(walk.id)
      onUpdated(updated.walk)
    } catch (ex) {
      setErr((ex as Error).message)
    } finally {
      setRunning(false)
    }
  }

  // v2 Module 11 — Photo-to-Progress Tracking
  const [progressBusy, setProgressBusy] = useState(false)
  const [progressResult, setProgressResult] = useState<Awaited<
    ReturnType<typeof api.photoProgress.detect>
  > | null>(null)
  const [applyingTaskIds, setApplyingTaskIds] = useState<Set<string>>(new Set())
  async function detectProgress() {
    setProgressBusy(true)
    setErr(null)
    try {
      const r = await api.photoProgress.detect(walk.project_id, { walk_id: walk.id })
      setProgressResult(r)
    } catch (ex) {
      setErr((ex as Error).message)
    } finally {
      setProgressBusy(false)
    }
  }
  async function applyProgressTasks() {
    if (!progressResult?.suggested_task_completions) return
    const ids = progressResult.suggested_task_completions
      .map((t) => t.task_id)
      .filter((id) => !applyingTaskIds.has(id))
    if (ids.length === 0) return
    if (!confirm(`Mark ${ids.length} task(s) complete?`)) return
    try {
      const r = await api.photoProgress.apply(walk.project_id, ids)
      setApplyingTaskIds(new Set([...applyingTaskIds, ...ids]))
      alert(`Updated ${r.updated} schedule task(s).`)
    } catch (ex) {
      setErr((ex as Error).message)
    }
  }

  return (
    <div className="card mb-3">
      <div className="row-between mb-2">
        <h3>AI Analysis</h3>
        <button className="btn-primary small" onClick={analyze} disabled={running}>
          {running ? 'Analyzing…' : walk.ai_summary ? 'Re-analyze' : 'Analyze with AI'}
        </button>
      </div>
      {err && <div className="card alert mb-2">{err}</div>}
      {!walk.ai_summary && !running && (
        <p className="muted small">
          Click "Analyze with AI" to have Claude review the photos against the Heritage Spec Book
          and Master Scope for this phase. Roughly $0.05 per run.
        </p>
      )}
      {walk.ai_summary && (
        <>
          <p style={{ whiteSpace: 'pre-wrap' }}>{walk.ai_summary}</p>
          {flags.length > 0 && (
            <div className="mt-3">
              <h4 className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Flags ({flags.length})
              </h4>
              {flags.map((f, i) => (
                <div key={i} className="flag-row">
                  <Badge color={FLAG_COLOR[f.severity]}>{f.severity.toUpperCase()}</Badge>
                  <div className="grow">
                    <div className="row-title">{f.item}</div>
                    <div className="muted small">{f.note}</div>
                  </div>
                  {(f.severity === 'critical' || f.severity === 'warning') && (
                    <button
                      className="btn-ghost small"
                      onClick={() => promoteToPunch(i, f)}
                      disabled={promoted.has(i)}
                      title="Add this as a punch list item"
                    >
                      {promoted.has(i) ? '✓ Added' : '+ Punch'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* v2 Module 11: Photo-to-Progress */}
      <div className="mt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
        <div className="row-between">
          <h4 style={{ margin: 0 }}>Progress vs prior walk</h4>
          <button className="btn-ghost small" onClick={detectProgress} disabled={progressBusy}>
            {progressBusy ? 'Comparing…' : 'Detect progress'}
          </button>
        </div>
        {progressResult && (
          <div className="mt-2">
            <p className="muted small">
              Compared to walk on {progressResult.older_walk.date}
              {progressResult.phase_pct_complete_estimate != null && (
                <> · est. phase {progressResult.phase_pct_complete_estimate}% complete</>
              )}
            </p>
            {progressResult.progress_observations &&
              progressResult.progress_observations.length > 0 && (
                <ul>
                  {progressResult.progress_observations.map((o, i) => (
                    <li key={i} className="small">
                      {o}
                    </li>
                  ))}
                </ul>
              )}
            {progressResult.suggested_task_completions &&
              progressResult.suggested_task_completions.length > 0 && (
                <div className="card mt-2" style={{ background: 'rgba(46,160,67,0.06)' }}>
                  <strong>Suggested task completions:</strong>
                  <ul>
                    {progressResult.suggested_task_completions.map((t, i) => (
                      <li key={i} className="small">
                        <strong>{t.task_name}</strong> — {t.evidence}
                      </li>
                    ))}
                  </ul>
                  <button className="btn-primary small" onClick={applyProgressTasks}>
                    Mark all suggested complete
                  </button>
                </div>
              )}
            {progressResult.notes && <p className="muted small mt-1">{progressResult.notes}</p>}
          </div>
        )}
      </div>
    </div>
  )
}
